// AiEndpointConfigPanel — config-vyn i AI-assistenten (endpoint/apiKey/
// model + test-connection). Extraherad ur AiAssistantDialog (#1 i
// PRE_RELEASE_REVIEW_100); state/logik bor i useAiEndpointConfig.

import { Settings, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AiConfig, TestStatus } from '@/hooks/useAiEndpointConfig';

interface AiEndpointConfigPanelProps {
  configDraft: AiConfig;
  setConfigDraft: React.Dispatch<React.SetStateAction<AiConfig>>;
  testStatus: TestStatus;
  isConfigured: boolean;
  onTestConnection: () => void;
  onSaveConfig: () => void;
  onBackToChat: () => void;
}

export function AiEndpointConfigPanel({
  configDraft,
  setConfigDraft,
  testStatus,
  isConfigured,
  onTestConnection,
  onSaveConfig,
  onBackToChat,
}: AiEndpointConfigPanelProps) {
  return (
    <div className="flex flex-col gap-5 p-6">
      <div className="text-center mb-2">
        <Settings className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        <h3 className="font-semibold text-base">AI Configuration</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Connect to any OpenAI-compatible API endpoint (Ollama, LM Studio, OpenAI, etc.)
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ai-endpoint" className="text-sm font-medium">
            API Endpoint
          </Label>
          <Input
            id="ai-endpoint"
            placeholder="e.g. http://192.168.1.4:11434"
            value={configDraft.endpoint}
            onChange={e => setConfigDraft(prev => ({ ...prev, endpoint: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            The base URL of your AI server. /v1/chat/completions is appended automatically if needed.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ai-apikey" className="text-sm font-medium">
            API Key
          </Label>
          <Input
            id="ai-apikey"
            placeholder="sk-... or leave empty for local models"
            value={configDraft.apiKey}
            onChange={e => setConfigDraft(prev => ({ ...prev, apiKey: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Your API key. For local models (Ollama) this can be any non-empty string.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ai-model" className="text-sm font-medium">
            Model (optional)
          </Label>
          <Input
            id="ai-model"
            placeholder="e.g. llama3, gpt-4o, claude-sonnet-4-20250514"
            value={configDraft.model}
            onChange={e => setConfigDraft(prev => ({ ...prev, model: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Model name to send in the request. Leave empty to use the server's default.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-2">
        <Button
          onClick={onTestConnection}
          variant="outline"
          className="flex-1 gap-2"
          disabled={!configDraft.endpoint.trim() || testStatus === 'testing'}
        >
          {testStatus === 'testing' && <span className="animate-spin w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full" />}
          {testStatus === 'success' && <Check className="w-3.5 h-3.5 text-green-500" />}
          {testStatus === 'error' && <AlertCircle className="w-3.5 h-3.5 text-destructive" />}
          {testStatus === 'idle' && 'Test Connection'}
          {testStatus === 'testing' && 'Testing...'}
          {testStatus === 'success' && 'Connected!'}
          {testStatus === 'error' && 'Failed'}
        </Button>
        <Button
          onClick={onSaveConfig}
          className="flex-1"
          disabled={!configDraft.endpoint.trim()}
        >
          Save &amp; Connect
        </Button>
      </div>

      {isConfigured && (
        <Button variant="ghost" size="sm" className="mx-auto" onClick={onBackToChat}>
          Back to chat
        </Button>
      )}
    </div>
  );
}
